import React from 'react';

import cx from 'classnames';

import Affirm from 'components/Icons/Affirm.svg';
import Afterpay from 'components/Icons/Afterpay.svg';
import Close from 'components/Icons/CloseV2.svg';
import Klarna from 'components/Icons/Klarna.svg';

import { EdgeBNPLCapabilities } from 'hooks/payment/bnplCapabilities/useBnplCapabilities';

import {
  BNPL_AVAILABLE,
  BNPL_AVAILABLE_DESCRIPTION,
  BNPL_MOBILE_AVAILABLE,
  BNPL_MOBILE_AVAILABLE_DESCRIPTION,
  CLOSE_BUTTON_LABEL,
  LEARN_MORE_LINK,
} from './BuyNowPayLaterBanner.dictionary';

import style from './BuyNowPayLaterBanner.module.css';

type BuyNowPayLaterBannerProps = {
  onDismissBanner: () => void;
  isDismissed?: boolean;
  capabilities?: EdgeBNPLCapabilities[];
  isLoading?: boolean;
};

const ICON_SIZE = 20;
const KlarnaIcon = () => <Klarna width={ICON_SIZE} height={ICON_SIZE} />;
const AffirmIcon = () => <Affirm width={ICON_SIZE} height={ICON_SIZE} />;
const AfterpayIcon = () => <Afterpay width={ICON_SIZE} height={ICON_SIZE} />;
const iconMap = {
  [EdgeBNPLCapabilities.KLARNA]: KlarnaIcon,
  [EdgeBNPLCapabilities.AFFIRM]: AffirmIcon,
  [EdgeBNPLCapabilities.AFTERPAY]: AfterpayIcon,
};
const BNPL_LEARN_MORE_LINK = 'https://glossgenius.com/how-buynowpaylater-works';

export const BuyNowPayLaterBanner = React.forwardRef<
  HTMLDivElement,
  BuyNowPayLaterBannerProps
>(({ onDismissBanner, isDismissed, capabilities, isLoading }, ref) => {
  // This is needed to correctly set the height of the banner
  // when the component is mounted.
  const isHidden = isLoading || isDismissed === undefined;

  // If the banner is dismissed, don't render it.
  if (isDismissed === true) {
    return null;
  }

  const MobileText = () => (
    <div className={style.mobileTextContainer}>
      <a href={BNPL_LEARN_MORE_LINK} target="_blank" rel="noopener noreferrer">
        <span className={style.link}>{BNPL_MOBILE_AVAILABLE}</span>
      </a>
      <span> {BNPL_MOBILE_AVAILABLE_DESCRIPTION}</span>
    </div>
  );

  const DesktopText = () => (
    <div className={style.desktopTextContainer}>
      <span>{BNPL_AVAILABLE} </span>
      <span className={style.secondaryText}>{BNPL_AVAILABLE_DESCRIPTION} </span>
      <a href={BNPL_LEARN_MORE_LINK} target="_blank" rel="noopener noreferrer">
        <span className={style.link}>{LEARN_MORE_LINK}</span>
      </a>
    </div>
  );

  return (
    <div
      data-testid="buy-now-pay-later-banner"
      ref={ref}
      className={cx(style.root, {
        [style.isHidden]: isHidden,
      })}
    >
      <div className={style.container}>
        <div className={style.logoContainer}>
          {capabilities?.map((capability) => {
            const Icon = iconMap[capability];

            return <Icon key={capability} />;
          })}
        </div>
        <MobileText />
        <DesktopText />
      </div>
      <button
        aria-label={CLOSE_BUTTON_LABEL}
        type="button"
        className={style.closeButton}
        onClick={onDismissBanner}
      >
        <Close />
      </button>
    </div>
  );
});

BuyNowPayLaterBanner.displayName = 'BuyNowPayLaterBanner';
